import React, { useState } from 'react';
import { FaExclamationTriangle, FaTimes } from 'react-icons/fa';

interface ConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmButtonClass?: string;
  requireTextConfirmation?: boolean;
  textToConfirm?: string;
  warningItems?: string[];
  isLoading?: boolean;
}

export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  confirmButtonClass = "bg-red-600 hover:bg-red-700",
  requireTextConfirmation = false,
  textToConfirm = "",
  warningItems = [],
  isLoading = false
}) => {
  const [confirmationInput, setConfirmationInput] = useState('');

  if (!isOpen) return null;

  const isConfirmDisabled = requireTextConfirmation 
    ? confirmationInput !== textToConfirm || isLoading
    : isLoading;

  const handleConfirm = () => {
    if (!isConfirmDisabled) {
      onConfirm();
      setConfirmationInput(''); // Reset input
    }
  };

  const handleClose = () => {
    setConfirmationInput(''); // Reset input
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <FaExclamationTriangle className="text-red-600 w-6 h-6" />
            <h3 className="text-lg font-semibold text-red-900">{title}</h3>
          </div>
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FaTimes className="w-5 h-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="mb-6">
          <p className="text-gray-700 mb-4">{message}</p>
          
          {/* Warning items */}
          {warningItems.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-red-800 font-medium mb-2">
                This action cannot be undone. This will permanently delete:
              </p>
              <ul className="text-sm text-red-700 ml-4">
                {warningItems.map((item, index) => (
                  <li key={index}>• {item}</li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Text confirmation */}
          {requireTextConfirmation && (
            <div>
              <label htmlFor="confirmation-input" className="block text-sm font-medium text-gray-700 mb-2">
                Please type <strong>{textToConfirm}</strong> to confirm:
              </label>
              <input
                id="confirmation-input"
                type="text"
                value={confirmationInput}
                onChange={(e) => setConfirmationInput(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                placeholder={`Type "${textToConfirm}" here`}
                disabled={isLoading}
              />
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end space-x-3">
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            {cancelText}
          </button>
          <button
            onClick={handleConfirm}
            disabled={isConfirmDisabled}
            className={`px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 disabled:bg-gray-400 flex items-center space-x-2 ${confirmButtonClass}`}
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border border-white border-t-transparent"></div>
                <span>Processing...</span>
              </>
            ) : (
              <span>{confirmText}</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationDialog;
